import React, { useState } from "react";
import { prenomsLus } from "../Utils/choixIdentite";

/**
 * Fenetre « Code lu » — ce que porte reellement le Datamatrix INS.
 *
 * Elle a d'abord vecu sur la tablette (retour du 01/09 : « presenter le code
 * complet que tu as lu sans interpretation du tout »). Elle a demenage ici le
 * 02/09, a la demande de Nicolas : « sur la tablette je trouve cela peu utile.
 * Peux tu le supprimer et mettre cette fonctionnalite dans le Web dans la
 * section "Secretariat (Dossiers)" ? »
 *
 * Il a raison, et pour une raison de metier : devant un patient, personne ne
 * releve un code brut caractere par caractere. Celui qui en a besoin est au
 * secretariat, apres coup, quand un dossier ne colle pas.
 *
 * Trois niveaux, et l'ordre est tout le sujet :
 *
 *   1. le code BRUT, caractere par caractere, tel que le lecteur l'a rendu ;
 *   2. les champs S1..S7 tels que decoupes, avant toute mise en forme ;
 *   3. les traits retenus par Lybo, annonces comme interpretes.
 *
 * Un ecart entre le haut et le bas de la fenetre est de NOTRE fait, pas de
 * celui du document. Sans le niveau 1, on ne peut pas distinguer « le document
 * ne porte pas la date » de « nous ne savons pas la lire », et ce sont deux
 * corrections differentes.
 *
 * Ce composant n'analyse RIEN : il affiche la lecture enregistree par la
 * tablette. Le decoupage vit deja a deux endroits (tablette et serveur) ; en
 * ajouter un troisieme ici garantissait qu'ils divergent.
 */

// Les caracteres de service sont invisibles a l'ecran alors qu'ils portent
// toute la structure du code. On les rend visibles sans les retirer : ce qui
// est affiche doit rester le code, pas une version presentable du code.
//
// Ils sont ecrits en echappements \uXXXX et non en litteral : un octet de
// controle depose tel quel dans un fichier source se fait manger au premier
// copier-coller, et ce module ne servirait plus a rien s'il mentait sur ce
// qu'il affiche.
const VISIBLES = {
  "\u001C": "\u241C", // FS
  "\u001D": "\u241D", // GS, separateur de champ ISO/IEC 15434
  "\u001E": "\u241E", // RS
  "\u001F": "\u241F", // US
  "\u0004": "\u2404", // EOT, fin de transmission
  "\n": "\u240A",
  "\r": "\u240D",
  "\t": "\u2409",
};

function rendreVisible(brut) {
  return String(brut || "").replace(
    /[\u0000-\u001F\u007F]/g,
    (c) => VISIBLES[c] || `\u241B${c.charCodeAt(0).toString(16).padStart(2, "0")}`
  );
}

const LIBELLES_CHAMPS = {
  1: "S1 — matricule INS",
  2: "S2 — OID (autorité d'affectation)",
  3: "S3 — prénom(s) de naissance",
  4: "S4 — nom de naissance",
  5: "S5 — sexe",
  6: "S6 — date de naissance",
  7: "S7 — lieu de naissance",
};

/** Une ligne copiable : un libelle, une valeur, un bouton. */
function Ligne({ libelle, valeur, absent, note }) {
  const [copie, setCopie] = useState(false);
  const texte = String(valeur ?? "");
  const copier = async () => {
    if (!texte) return;
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(true);
      setTimeout(() => setCopie(false), 1500);
    } catch {
      // Le presse-papiers peut etre refuse par le navigateur. La valeur reste
      // selectionnable a la souris : on ne bloque pas, on n'alerte pas.
    }
  };
  return (
    <div className="cl-ligne">
      <div className="cl-ligneTexte">
        <div className="cl-libelle">
          {libelle}
          {note ? <span className="cl-note"> — {note}</span> : null}
        </div>
        <div className={`cl-valeur${texte ? "" : " cl-absent"}`}>
          {texte || absent || "— absent du code —"}
        </div>
      </div>
      {texte ? (
        <button type="button" className="cl-copier" onClick={copier}>
          {copie ? "Copié" : "Copier"}
        </button>
      ) : null}
    </div>
  );
}

export default function CodeLuINS({ lecture, titre, onClose }) {
  if (!lecture) return null;

  const champs = lecture.champs || {};
  const traits = lecture.traits || {};
  const provenance = lecture.provenanceChamps || {};
  const anomalies = lecture.anomalies || [];
  const brut = lecture.brut || "";

  // D'ou vient chaque trait : c'est ce qui permet de comprendre un document qui
  // ne suit pas la specification a la lettre, sans avoir a le deviner depuis un
  // rapport de bug.
  const venuDe = (nom) => {
    const n = provenance[nom];
    return n ? `lu dans S${n}` : "";
  };

  const numeros = Object.keys(champs).sort();

  return (
    <div className="cl-fond" role="dialog" aria-modal="true" aria-label={`${titre || "Identité INS"} — le contenu du Data Matrix`}>
      <div className="cl-fenetre">
        <div className="cl-entete">
          {/* Libelle demande le 04/09 : « Identité INS — Voir le contenu du
              Data Matrix ». Le bouton qui ouvre cette fenetre porte le
              meme, pour qu'on reconnaisse ou l'on a clique. */}
          <h3>{titre || "Identité INS"} — le contenu du Data Matrix</h3>
          <button
            type="button"
            className="cl-fermer"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="cl-corps">
          {anomalies.length > 0 && (
            <div className="cl-alerte">
              <strong>J'ai lu le code, mais tout n'est pas net</strong>
              <ul>
                {anomalies.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── 1. Le code brut ─────────────────────────────────────────── */}
          <div className="cl-section">
            <div className="cl-sectionTitre">
              1. Le code brut, sans interprétation
            </div>
            <p className="cl-explication">
              Tel que le lecteur l'a rendu. Les caractères de service, invisibles
              à l'impression mais qui portent toute la structure du code, sont
              rendus visibles. La longueur est affichée aussi : c'est elle qui
              trahit un code tronqué.
            </p>
            <div className="cl-brut">
              {rendreVisible(brut) || "— aucun code enregistré —"}
            </div>
            <div className="cl-longueur">{brut.length} caractères</div>
            {brut ? (
              <button
                type="button"
                className="cl-copier cl-copierLarge"
                onClick={() => navigator.clipboard?.writeText(brut)}
              >
                Copier le code brut
              </button>
            ) : null}
          </div>

          {/* ── 2. Les champs S1..S7 ────────────────────────────────────── */}
          <div className="cl-section">
            <div className="cl-sectionTitre">2. Les champs, tels que découpés</div>
            <p className="cl-explication">
              Avant toute mise en forme. Un segment que Lybo n'a pas su rattacher
              à un trait apparaît quand même : c'est souvent lui qui explique un
              document qu'on ne comprend pas.
            </p>
            {numeros.length ? (
              numeros.map((n) => (
                <Ligne
                  key={n}
                  libelle={LIBELLES_CHAMPS[n] || `S${n}`}
                  valeur={champs[n]}
                />
              ))
            ) : (
              <div className="cl-absent">— aucun champ découpé —</div>
            )}
          </div>

          {/* ── 3. Ce que Lybo en a retenu ──────────────────────────────── */}
          <div className="cl-section">
            <div className="cl-sectionTitre">3. Ce que Lybo en a retenu</div>
            <p className="cl-explication">
              Interprété. Un écart entre cette partie et celle du dessus est de
              notre fait, pas de celui du document.
            </p>
            <Ligne
              libelle="Matricule INS"
              valeur={traits.insNumber}
              note={venuDe("matricule")}
            />
            <Ligne
              libelle="Prénoms de naissance"
              valeur={prenomsLus(traits.firstNames)}
              note={venuDe("prenoms")}
            />
            <Ligne
              libelle="Nom de naissance"
              valeur={traits.lastName}
              note={venuDe("nom")}
            />
            <Ligne libelle="Sexe" valeur={traits.sex} note={venuDe("sexe")} />
            <Ligne
              libelle="Date de naissance"
              valeur={traits.birthday}
              note={venuDe("naissance")}
            />
            <Ligne libelle="OID" valeur={traits.oid} note={venuDe("oid")} />
            {/*
              Precision qui n'est pas un detail, et qui revenait a chaque
              echange : le Datamatrix INS ne porte QUE l'identite de naissance.
              Il n'y a jamais de nom d'usage dedans, par construction. Ce n'est
              pas une lecture ratee, c'est le document qui ne le porte pas.
            */}
            <Ligne
              libelle="Nom d'usage"
              valeur=""
              absent="— le Datamatrix INS ne porte jamais le nom d'usage —"
            />
          </div>

          {lecture.luLe ? (
            <div className="cl-pied">
              Code lu le {new Date(lecture.luLe).toLocaleString("fr-FR")}.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
